import { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { toEthiopianDate, amharicMonths, englishMonths, getEthiopianDayOfWeek } from '../../utils/dateConverter';
import ForAll from '../../assets/data/ForAll.json';

const weekdayMap = {
  'ሰኞ': 2, 'ማክሰኞ': 3, 'እሮብ': 4, 'ኀሙስ': 5, 'አርብ': 6, 'ቅዳሜ': 7, 'እሑድ': 8
};

// Component to render fetched data
const ContentRenderer = ({ title, item: rawItem, loading, error }) => {
  if (loading) return <div className="text-center py-6 text-gray-500">ይዘቱ ከመረጃ ቋት (DB) እየተጫነ ነው...</div>;
  if (error) return <div className="text-center py-6 text-red-500">{error}</div>;
  
  const item = Array.isArray(rawItem) ? rawItem[0] : rawItem;
  if (!item) return <div className="no-content text-center py-6 text-gray-400">ምንም ይዘት አልተገኘም</div>;

  return (
    <div className="content-display space-y-3">
      <h2 className="text-xl font-bold text-gray-800 border-b pb-2">{title || item.title}</h2>
      {item.title && item.title !== title && (
        <h3 className="text-base font-semibold text-blue-800">{item.title}</h3>
      )}

      <div className="content space-y-3">
        {Array.isArray(item.content) ? (
          item.content.map((sec, idx) => (
            <div key={idx} className="content-section p-3 bg-gray-50 rounded-lg">
              {sec?.title && <strong className="block font-bold text-amber-900 mb-1">{sec.title}</strong>}
              <p className="text-sm leading-relaxed whitespace-pre-line text-gray-800">
                {typeof sec === 'string' ? sec : (sec?.main || JSON.stringify(sec))}
              </p>
            </div>
          ))
        ) : (
          <p className="text-sm leading-relaxed whitespace-pre-line text-gray-800">
            {typeof item.content === 'string' ? item.content : JSON.stringify(item.content)}
          </p>
        )}
      </div>
    </div>
  );
};

const FetchStaticData = ({ data, DatasId }) => {
  const t = useTranslation();
  const hymn = data?.hymns?.find(item => item.id === DatasId);
  if (!hymn) return <div className="no-content">{t('no_readings')}</div>;

  return (
    <div className="content-display space-y-3">
      <h2 className="text-xl font-bold text-gray-800 border-b pb-2">{hymn.title}</h2>
      <div className="hymn-lines space-y-1">
        {hymn.content?.map((line, idx) => (
          <p key={idx} className="hymn-line text-sm text-gray-800">{line}</p>
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const t = useTranslation();
  const [currentTime] = useState(new Date());
  const [output, setOutput] = useState(null);
  const gcDateStr = currentTime.toLocaleDateString('en-GB');

  const ethDate = toEthiopianDate(currentTime);
  const ecMonthNameAmharic = amharicMonths[ethDate.ecMonth - 1];
  const ecMonthName = englishMonths[ethDate.ecMonth - 1];
  const ecDayOfWeek = getEthiopianDayOfWeek(currentTime);
  const apiURL = process.env.REACT_APP_API_URL || '/api';

  const fetchAndDisplay = async (type, title, params = {}) => {
    setOutput(<ContentRenderer title={title} loading={true} />);
    try {
      const query = new URLSearchParams({ type, ...params }).toString();
      const res = await fetch(`${apiURL}/content?${query}`);
      if (!res.ok) throw new Error('Failed to fetch from DB');
      const json = await res.json();
      const item = Array.isArray(json.data) ? json.data[0] : (json.data || json.item);
      setOutput(<ContentRenderer title={title} item={item} />);
    } catch (err) {
      setOutput(<ContentRenderer title={title} error="መረጃውን ከመረጃ ቋት ማምጣት አልተቻለም" />);
    }
  };

  const showThursdayPraise = () => {
    const dayId = weekdayMap[ecDayOfWeek] || 1;
    fetchAndDisplay('wudasie', `የ${ecDayOfWeek} ውዳሴ ማርያም`, { itemId: dayId });
  };

  const showGitsawe = () => {
    fetchAndDisplay('gitsawe', `የዛሬ ግጻዌ (${ecMonthNameAmharic} ${ethDate.ecDay})`, {
      month: ecMonthName,
      day: ethDate.ecDay
    });
  };

  const showSinksar = () => {
    fetchAndDisplay('sinksar', `የዛሬ ስንክሳር (${ecMonthNameAmharic} ${ethDate.ecDay})`, {
      month: ecMonthName,
      day: ethDate.ecDay
    });
  };

  const ShowBefore = () => {
    setOutput(<FetchStaticData data={ForAll} DatasId={1} />);
  };

  const showForAllById = (id) => {
    setOutput(<FetchStaticData data={ForAll} DatasId={id} />);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Date Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="home-date-card rounded-xl shadow-sm border border-gray-100 p-4 text-center">
          <div className="text-xs text-gray-400 mb-1">Gregorian Calendar (GC)</div>
          <div className="text-lg font-bold">{gcDateStr}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-sm p-4 text-center text-white">
          <div className="text-xs text-blue-100 mb-1">ዕለቱ / Day</div>
          <div className="text-2xl font-bold">{ecDayOfWeek}</div>
        </div>
        <div className="home-date-card rounded-xl shadow-sm border border-gray-100 p-4 text-center">
          <div className="text-xs text-gray-400 mb-1">በኢትዮጵያ አቆጣጠር (EC)</div>
          <div className="text-lg font-bold">{`${ecMonthNameAmharic} ${ethDate.ecDay}, ${ethDate.ecYear}`}</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-6">
        {[
          { label: t('buttons.daily_prayer'), onClick: ShowBefore, color: 'from-amber-500 to-orange-500' },
          { label: t('buttons.wudasie', { day: ecDayOfWeek }), onClick: showThursdayPraise, color: 'from-purple-500 to-indigo-500' },
          { label: t('buttons.anqetsa_birhan'), onClick: () => showForAllById(8), color: 'from-teal-500 to-cyan-500' },
          { label: t('buttons.yewedeswa_melak'), onClick: () => showForAllById(9), color: 'from-pink-500 to-rose-500' },
          { label: t('buttons.melka_mariam'), onClick: () => showForAllById(10), color: 'from-violet-500 to-purple-500' },
          { label: t('buttons.melka_jesus'), onClick: () => showForAllById(11), color: 'from-blue-500 to-cyan-500' },
          { label: t('buttons.gitsawe'), onClick: showGitsawe, color: 'from-green-500 to-emerald-500' },
          { label: t('buttons.sinksar'), onClick: showSinksar, color: 'from-red-500 to-orange-500' },
        ].map((btn, i) => (
          <button
            key={i}
            onClick={btn.onClick}
            className={`bg-gradient-to-r ${btn.color} text-white text-sm font-medium py-2.5 px-3 rounded-xl shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-200`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Output Display */}
      {output && (
        <div className="rounded-xl shadow-sm border border-gray-100 p-5 home-output-panel bg-white">
          {output}
        </div>
      )}
    </div>
  );
};

export default Home;
